import React, { createContext, useContext, useEffect, useState } from 'react';
import { DARK_THEME_IDS, THEME_IDS, DEFAULT_THEME, STORAGE_KEY } from './tokens';

const VALID = new Set(THEME_IDS);

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  resolvedTheme: DEFAULT_THEME,
  setTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID.has(stored)) return stored;
    // One-time bridge from the pre-theme boolean toggle so returning users
    // keep their light/dark preference instead of resetting to the default.
    const legacy = localStorage.getItem('darkMode');
    if (legacy === 'true') return 'dark';
    if (legacy === 'false') return 'light';
    return DEFAULT_THEME;
  });

  // No 'system' resolution here (discover-chat has no system option yet);
  // resolvedTheme == theme, kept as a distinct name for parity with discover.
  const resolvedTheme = theme;

  useEffect(() => {
    const html = window.document.documentElement;
    const isDark = DARK_THEME_IDS.includes(resolvedTheme);
    localStorage.setItem(STORAGE_KEY, theme);
    // data-theme drives the token blocks (theme.css); .dark/.light drive
    // Tailwind dark: variants; .dark-mode on <body> drives legacy elastiq.css.
    html.classList.remove('light', 'dark');
    html.classList.add(isDark ? 'dark' : 'light');
    html.setAttribute('data-theme', resolvedTheme);
    window.document.body.classList.toggle('dark-mode', isDark);
  }, [theme, resolvedTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function useIsDarkTheme() {
  const { resolvedTheme } = useTheme();
  return DARK_THEME_IDS.includes(resolvedTheme);
}
